import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../../../store/hooks';
import ConfigurationTable from '../../components/ConfigurationTable/ConfigurationTable';
import {
    IConfigRow,
    RowAction,
    useDefaultColumns,
} from '../../components/ConfigurationTable/ConfigurationTable.utils';
import { useCallback, useEffect, useMemo } from 'react';
import {
    addOrCreateUnableToNotifyReason,
    loadUnableToNotifyReasons,
    removeUnableToNotifyReason,
} from '../../../../store/features/customerUpdates/customerUpdates.thunks';
import CreateOrUpdateDialog, {
    CreateOrUpdateValueValidator,
} from '../../components/CreateOrUpdateDialog/CreateOrUpdateDialog';
import { useCreateOrUpdateDialog } from '../../components/CreateOrUpdateDialog/useCreateOrUpdateDialog';
import ConfirmDeletionDialog from '../../components/ConfirmDeletionDialog/ConfirmDeletionDialog';
import { useConfirmDeletionDialog } from '../../components/ConfirmDeletionDialog/useConfirmDeletionDialog';
import { ToolbarButton } from '@fluentui/react-components';
import { Add24Regular } from '@fluentui/react-icons';

const reasonNameValidCharacterCount = { min: 3, max: 100 } as const;
const maxCount = 100;

const normaliseName = (name: string) => name.toLowerCase();

const reasonValidator = (
    isEdit: boolean,
    value: string,
    existingNormalisedNames: Set<string>,
    totalCount: number,
    t: (k: string) => string
) => {
    if (totalCount >= maxCount) {
        return t('UnableToNotifyReason_Validation_MaxCountReached').replace(
            '{count}',
            maxCount.toString()
        );
    }

    if (
        !value ||
        value.length < reasonNameValidCharacterCount.min ||
        value.length > reasonNameValidCharacterCount.max
    ) {
        return t('UnableToNotifyReason_Validation_InvalidName')
            .replace('{min}', reasonNameValidCharacterCount.min.toString())
            .replace('{max}', reasonNameValidCharacterCount.max.toString());
    }

    if (!isEdit && existingNormalisedNames.has(normaliseName(value))) {
        return t('UnableToNotifyReason_Validation_AlreadyExist');
    }

    return null;
};

const UnableToNotifyReasonsPanel: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(loadUnableToNotifyReasons());
    }, [dispatch]);

    const { unableToNotifyReasons } = useSelector((x) => x.customerUpdates);
    const { searchQuery } = useSelector((x) => x.appConfiguration);

    const { items, existingNormalisedNames, totalCount } = useMemo(() => {
        const rows: IConfigRow[] = [];
        const nameSet = new Set<string>();

        for (const item of unableToNotifyReasons.data) {
            rows.push({
                id: item.id,
                value: item.displayName,
            });

            nameSet.add(normaliseName(item.displayName));
        }

        return {
            items: rows,
            existingNormalisedNames: nameSet,
            totalCount: unableToNotifyReasons.data.length,
        };
    }, [unableToNotifyReasons.data]);

    const filteredItems = useMemo(() => {
        const normalisedQuery = normaliseName(searchQuery);
        return items.filter((x) =>
            normaliseName(x.value).includes(normalisedQuery)
        );
    }, [items, searchQuery]);

    const valueValidator = useCallback<CreateOrUpdateValueValidator>(
        (value, id) =>
            reasonValidator(
                !!id,
                value,
                existingNormalisedNames,
                totalCount,
                t
            ),
        [existingNormalisedNames, totalCount, t]
    );

    const {
        isOpen: upsertDialogOpen,
        value: upsertValue,
        openDialog: openUpsertDialog,
        closeDialog: closeUpsertDialog,
    } = useCreateOrUpdateDialog();

    const {
        isOpen: deletionDialogOpen,
        itemId: deletionItemId,
        message: deletionDialogMessage,
        openDialog: openDeletionDialog,
        closeDialog: closeDeletionDialog,
    } = useConfirmDeletionDialog(
        useCallback(
            (name: string) =>
                t('UnableToNotifyReason_DeletionDialog_Message').replace(
                    '{name}',
                    name
                ),
            [t]
        )
    );

    const columns = useDefaultColumns({
        valueHeader: t('TableHeader_UnableToNotifyReasons'),
        onEdit: useCallback<RowAction>(
            (row) =>
                openUpsertDialog({ itemId: row.id, initialValue: row.value }),
            [openUpsertDialog]
        ),
        onDelete: useCallback<RowAction>(
            (row) =>
                openDeletionDialog({ itemId: row.id, itemName: row.value }),
            [openDeletionDialog]
        ),
    });

    const deleteReason = useCallback(() => {
        if (!deletionItemId) {
            return;
        }

        dispatch(removeUnableToNotifyReason(deletionItemId)).then(() => {
            dispatch(loadUnableToNotifyReasons());
            closeDeletionDialog();
        });
    }, [deletionItemId, closeDeletionDialog, dispatch]);

    const upsertReason = useCallback(
        (id?: string, value?: string) => {
            if (!value) {
                return;
            }

            dispatch(
                addOrCreateUnableToNotifyReason({ id, displayName: value })
            ).then(() => {
                dispatch(loadUnableToNotifyReasons());
                closeUpsertDialog();
            });
        },
        [closeUpsertDialog, dispatch]
    );

    return (
        <>
            <ConfigurationTable
                items={filteredItems}
                columns={columns}
                loadStatus={unableToNotifyReasons.loadStatus}
                bottomActions={
                    <ToolbarButton
                        onClick={() => openUpsertDialog(null)}
                        icon={<Add24Regular />}
                    >
                        {t('ToolbarButton_AddUnableToNotifyReason')}
                    </ToolbarButton>
                }
            />
            <CreateOrUpdateDialog
                open={upsertDialogOpen}
                itemId={upsertValue?.itemId}
                initialValue={upsertValue?.initialValue}
                labels={{
                    title: t('UnableToNotifyReason_UpsertDialog_Title').replace(
                        '{mode}',
                        t(
                            upsertValue?.itemId
                                ? 'UpsertDialog_Edit'
                                : 'UpsertDialog_Add'
                        )
                    ),
                    value: t('UnableToNotifyReason_UpsertDialog_Label'),
                }}
                valueValidator={valueValidator}
                onSave={upsertReason}
                onClose={closeUpsertDialog}
            />
            <ConfirmDeletionDialog
                open={deletionDialogOpen}
                message={deletionDialogMessage}
                onConfirm={deleteReason}
                onClose={closeDeletionDialog}
            />
        </>
    );
};

export default UnableToNotifyReasonsPanel;
